/**
 * ScaliqPushPopover — "Push naar ScalIQ"-knop + campagne-picker.
 *
 * Gebruikt op de Sales Pipeline (lead-drawer + bulk-bar). Kiest een ScalIQ-
 * campagne en pusht de geselecteerde leads/kandidaten via POST /scaliq/push
 * (1 ScalIQ-credit per nieuw toegevoegde lead; duplicaten kosten niets).
 */
import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import i18n from "i18next";
import { Rocket, Loader2, ChevronDown, CheckCircle2 } from "lucide-react";

export default function ScaliqPushPopover({ ids, kind = "lead", onDone, compact = false }) {
    const [open, setOpen] = useState(false);
    const [campaigns, setCampaigns] = useState(null);
    const [loading, setLoading] = useState(false);
    const [pushing, setPushing] = useState(null);
    const [confirmFor, setConfirmFor] = useState(null);
    const [notConfigured, setNotConfigured] = useState(false);
    const boxRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) { setOpen(false); setConfirmFor(null); } };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [open]);

    const toggle = async () => {
        if (open) { setOpen(false); setConfirmFor(null); return; }
        if (ids.length > 25) { toast.error(i18n.t("scaliq.push_too_many", { max: 25 })); return; }
        setOpen(true);
        if (campaigns === null) {
            setLoading(true);
            try {
                const r = await api.get("/scaliq/campaigns");
                setCampaigns(r.data.campaigns || []);
            } catch (e) {
                if (e?.response?.status === 400) { setNotConfigured(true); setCampaigns([]); }
                else { toast.error(e?.response?.data?.detail || i18n.t("scaliq.campaigns_failed")); setOpen(false); }
            } finally { setLoading(false); }
        }
    };

    const push = async (campaign) => {
        setPushing(campaign.id);
        try {
            const r = await api.post("/scaliq/push", { campaign_id: campaign.id, kind, ids });
            const d = r.data;
            const parts = [];
            if (d.created.length) parts.push(i18n.t("scaliq.push_created", { n: d.created.length }));
            if (d.duplicates.length) parts.push(i18n.t("scaliq.push_dupes", { n: d.duplicates.length }));
            if (d.errors.length) parts.push(i18n.t("scaliq.push_errors", { n: d.errors.length }));
            const msg = `${campaign.name.slice(0, 40)}: ${parts.join(" · ") || i18n.t("scaliq.push_nothing")}`;
            if (d.created.length) toast.success(msg, { duration: 6000 });
            else toast.warning(msg, { duration: 6000 });
            if (d.errors.length) {
                toast.error(d.errors.slice(0, 2).map((e) => `${e.name || e.id}: ${e.error}`).join(" — "), { duration: 8000 });
            }
            setOpen(false);
            setConfirmFor(null);
            onDone?.(d);
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("scaliq.push_failed")); }
        finally { setPushing(null); }
    };

    const active = (campaigns || []).filter((c) => c.status !== "CAMPAIGN_ARCHIVED");

    return (
        <div className="relative inline-block" ref={boxRef}>
            <button onClick={toggle} disabled={!ids?.length}
                data-testid="scaliq-push-open"
                title={i18n.t("scaliq.push_tip")}
                className={`inline-flex items-center gap-1.5 rounded-full font-medium border border-[#6366f1]/50 text-[#818cf8] hover:bg-[#6366f1]/10 disabled:opacity-50 ${compact ? "px-2.5 py-1 text-[11px]" : "px-3.5 py-2 text-xs"}`}>
                <Rocket size={compact ? 11 : 13} /> {i18n.t("scaliq.push_btn")} <ChevronDown size={compact ? 10 : 12} />
            </button>
            {open && (
                <div className="absolute z-50 mt-2 right-0 w-80 max-h-80 overflow-y-auto rounded-xl border border-white/15 bg-[var(--jr-surface)] shadow-2xl p-2"
                    data-testid="scaliq-push-popover">
                    <div className="px-2 py-1.5 text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">
                        {i18n.t("scaliq.pick_campaign", { n: ids.length })}
                    </div>
                    {loading ? (
                        <div className="grid place-items-center py-6"><Loader2 size={16} className="animate-spin text-[var(--jr-text-3)]" /></div>
                    ) : notConfigured ? (
                        <p className="px-2 py-3 text-xs text-[var(--jr-text-3)]">{i18n.t("scaliq.not_configured")}</p>
                    ) : active.length === 0 ? (
                        <p className="px-2 py-3 text-xs text-[var(--jr-text-3)]">{i18n.t("scaliq.no_campaigns")}</p>
                    ) : active.map((c) => (
                        confirmFor?.id === c.id ? (
                            <div key={c.id} className="rounded-lg border border-[#6366f1]/50 bg-[#6366f1]/10 px-2.5 py-2"
                                data-testid={`scaliq-push-confirm-${c.id}`}>
                                <p className="text-[11px] text-white mb-1.5">
                                    {i18n.t("scaliq.push_confirm_text", { n: ids.length, name: c.name.slice(0, 40) })}
                                </p>
                                <div className="flex gap-2">
                                    <button onClick={() => push(c)} disabled={pushing !== null}
                                        data-testid={`scaliq-push-confirm-yes-${c.id}`}
                                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#6366f1] text-white text-[11px] font-medium hover:bg-[#6366f1]/85 disabled:opacity-50">
                                        {pushing === c.id ? <Loader2 size={10} className="animate-spin" /> : <Rocket size={10} />}
                                        {i18n.t("scaliq.push_confirm_yes")}
                                    </button>
                                    <button onClick={() => setConfirmFor(null)} disabled={pushing !== null}
                                        data-testid={`scaliq-push-confirm-no-${c.id}`}
                                        className="px-3 py-1 rounded-full border border-white/15 text-[var(--jr-text-2)] text-[11px] hover:text-white">
                                        {i18n.t("common.cancel")}
                                    </button>
                                </div>
                            </div>
                        ) : (
                        <button key={c.id} onClick={() => setConfirmFor(c)} disabled={pushing !== null}
                            data-testid={`scaliq-push-campaign-${c.id}`}
                            className="w-full text-left rounded-lg px-2.5 py-2 hover:bg-[#6366f1]/10 disabled:opacity-50">
                            <div className="flex items-center gap-2">
                                {pushing === c.id
                                    ? <Loader2 size={12} className="animate-spin text-[#818cf8] shrink-0" />
                                    : <CheckCircle2 size={12} className={`shrink-0 ${c.status === "CAMPAIGN_IN_PROGRESS" ? "text-[#22c55e]" : "text-[var(--jr-text-3)]"}`} />}
                                <span className="text-xs text-white truncate">{c.name}</span>
                            </div>
                            <div className="ml-5 text-[10px] text-[var(--jr-text-3)] font-mono">
                                {c.totalLeads} leads · {(c.stats || {}).replyRate ?? "-"}% reply
                            </div>
                        </button>
                        )
                    ))}
                </div>
            )}
        </div>
    );
}
